import { ErrorLike, NetworkStatus } from "@apollo/client";
import clsx from "clsx";

import { networkStatusToStatus } from "@smart/itops-hooks-dom";
import {
  buildErrorCode,
  checkErrorCode,
  UserErrorLevel,
} from "@smart/itops-utils-basic";

import { Icon } from "./icon";
import { Loading } from "./loading";

type StatusOverlayProps = {
  status: "initial" | "loading" | "error" | "success";
  errorCode?: string;
  description?: string;
};

export const StatusOverlay = ({
  status,
  description,
  errorCode,
}: StatusOverlayProps) => {
  switch (status) {
    case "loading":
      return <Loading description={description} />;
    case "error": {
      const error = checkErrorCode(errorCode);
      const getColor = (level: UserErrorLevel) => {
        switch (level) {
          case "SUCCESS":
            return "text-green-600";
          case "INFO":
            return "text-neutral-750";
          case "DEFAULT":
            return "text-neutral-850";
          case "WARN":
            return "text-yellow-450";
          default:
            return "text-red-500";
        }
      };

      return (
        <div
          className={clsx(
            "absolute inset-0 z-modal flex flex-col justify-center items-center",
            getColor(error.level),
          )}
        >
          <div className="p-[2rem] max-w-[50rem] w-full text-center">
            <Icon
              name="regularCircleExclamation"
              className="w-[5rem] h-[5rem]"
            />
            <p className="text-ui font-semibold my-[1rem]">{error.message}</p>
          </div>
        </div>
      );
    }
    default:
      return null;
  }
};

type GqlStatusOverlayProps = {
  error?: ErrorLike;
  networkStatus?: NetworkStatus;
  called?: boolean;
  loading?: boolean;
};

export const GqlStatusOverlay = ({
  error,
  networkStatus,
  called,
  loading,
}: GqlStatusOverlayProps) => {
  const status = networkStatusToStatus(networkStatus, {
    error,
    called,
    loading,
  });
  const errorCode = buildErrorCode(error);

  return <StatusOverlay status={status} errorCode={errorCode} />;
};
